/**
 * Geolocation helper — required for clock-in/out per Iter 60 spec.
 *
 * Contract:
 *   requestLocation() → Promise<{ok:true,  lat, lng, address}>
 *                    → Promise<{ok:false, code:'permission_denied'|'unavailable'|'timeout', message}>
 *
 * On success, attempts reverse-geocode via backend Nominatim proxy.
 * If geocoding fails, resolves with a raw-coords address — per spec,
 * geocode failure MUST NOT block clock-in; only outright permission
 * denial / unsupported hardware blocks.
 */
const API = process.env.REACT_APP_BACKEND_URL;

export const GEO_BLOCKERS = new Set(['permission_denied', 'unavailable', 'timeout']);

export function requestLocation({ timeoutMs = 12000 } = {}) {
  return new Promise((resolve) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      resolve({
        ok: false, code: 'unavailable',
        message: 'Your browser does not expose GPS. Try a modern browser or a device with location services.',
      });
      return;
    }
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        const accuracy = pos.coords.accuracy;
        let address = `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
        let geocoding_status = 'not_attempted';
        try {
          const r = await fetch(
            `${API}/api/geocode/reverse?lat=${lat}&lng=${lng}`,
            { headers: { Authorization: `Bearer ${localStorage.getItem('stellar_token')}` } }
          );
          if (r.ok) {
            const body = await r.json();
            if (body.address) address = body.address;
            geocoding_status = body.status || 'ok';
          } else {
            geocoding_status = `http_${r.status}`;
          }
        } catch {
          geocoding_status = 'network_error';
          address = `${lat.toFixed(4)}, ${lng.toFixed(4)} (geocoding unavailable)`;
        }
        resolve({ ok: true, lat, lng, accuracy, address, geocoding_status });
      },
      (err) => {
        const map = {
          1: { code: 'permission_denied', message: 'Location access was denied.' },
          2: { code: 'unavailable',       message: 'GPS signal is not available right now.' },
          3: { code: 'timeout',           message: 'Getting your location timed out.' },
        };
        resolve({ ok: false, ...(map[err.code] || { code: 'unavailable', message: err.message || 'Location unavailable' }) });
      },
      { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 0 }
    );
  });
}

/** Returns browser-specific instructions for enabling location. */
export function getLocationHelpUrl() {
  const ua = (navigator.userAgent || '').toLowerCase();
  if (ua.includes('firefox')) return 'https://support.mozilla.org/en-US/kb/does-firefox-share-my-location-websites';
  if (ua.includes('edg/')) return 'https://support.microsoft.com/en-us/microsoft-edge/location-and-privacy-in-microsoft-edge-31b5d154-0b1b-90ef-e389-7c7d4ffe7b04';
  if (ua.includes('safari') && !ua.includes('chrome')) return 'https://support.apple.com/en-ca/guide/safari/ibrw1049/mac';
  return 'https://support.google.com/chrome/answer/142065';
}
